import { type QuerySubStatusListItem, type QuerySubStatusListQuery } from '@utazzo/shared';
import { UzStatusBadge } from '@utazzo/ui';
import React, { useMemo } from 'react';

import { QuerySubStatusFormDrawer } from '../components/QuerySubStatusFormDrawer';
import { listQuerySubStatuses } from '../query-sub-statuses.api';
import {
  useBulkDeleteQuerySubStatuses,
  useBulkSetQuerySubStatusesStatus,
  useDeleteQuerySubStatus,
  useQueryStatusOptions,
  useQuerySubStatusesList,
} from '../use-query-sub-statuses';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCurrentUser } from '@/features/auth/use-auth';

const STATUS_OPTIONS = [
  { value: '', label: 'Active + inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
] as const;

/** Server search only fires from this many characters. */
const MIN_SEARCH_LENGTH = 3;

export const QuerySubStatusesListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN';
  const deleteQuerySubStatus = useDeleteQuerySubStatus();
  const bulkDeleteMut = useBulkDeleteQuerySubStatuses();
  const bulkSetStatusMut = useBulkSetQuerySubStatusesStatus();
  const statusOptionsQuery = useQueryStatusOptions();

  const parentStatusOptions: ReadonlyArray<{ value: string; label: string }> = useMemo(() => {
    const entries = (statusOptionsQuery.data ?? []).map((s) => ({ value: s.id, label: s.name }));
    return [{ value: '', label: 'All statuses' }, ...entries];
  }, [statusOptionsQuery.data]);

  const filters: ReadonlyArray<DataGridFilter> = useMemo(
    () => [
      {
        id: 'parentStatus',
        label: 'Status',
        options: parentStatusOptions,
        widthClass: 'w-56',
      },
      {
        id: 'status',
        label: 'Active',
        options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
        widthClass: 'w-48',
      },
    ],
    [parentStatusOptions],
  );

  const columns: ListScreenColumns<QuerySubStatusListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<QuerySubStatusListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.name}</span>
            <span className="text-xs text-zinc-500">{row.code}</span>
          </div>
        ),
      },
      {
        id: 'queryStatus',
        header: 'Parent status',
        cell: (row) => <span className="text-zinc-700">{row.queryStatus.name}</span>,
      },
      {
        id: 'description',
        header: 'Description',
        cell: (row) => <span className="text-zinc-600">{row.description ?? '-'}</span>,
      },
      {
        id: 'color',
        header: 'Color',
        cell: (row) =>
          row.color ? (
            <span className="inline-flex items-center gap-2">
              <span
                className="inline-block h-3.5 w-3.5 rounded-full border border-zinc-200"
                style={{ backgroundColor: row.color }}
                aria-hidden
              />
              <span className="font-mono text-xs text-zinc-600">{row.color}</span>
            </span>
          ) : (
            <span className="text-xs text-zinc-400">inherits parent</span>
          ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <UzStatusBadge status={row.isActive ? 'active' : 'inactive'} />,
      },
    ];
    return cols;
  };

  return (
    <ListScreen<QuerySubStatusListItem, QuerySubStatusListQuery>
      title="Query Sub Statuses"
      description="Finer breakdown of a query status (e.g. Quoted → negotiation)."
      useList={useQuerySubStatusesList}
      defaultSort={{ by: 'sortOrder', dir: 'asc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const status = state.filters.status;
        const parentStatus = state.filters.parentStatus;
        return {
          ...base,
          ...(parentStatus ? { queryStatusId: parentStatus } : {}),
          ...(status === 'active' ? { isActive: true } : {}),
          ...(status === 'inactive' ? { isActive: false } : {}),
        };
      }}
      columns={columns}
      filters={filters}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add sub status' }}
      renderDrawer={({ open, mode, row, close }) => (
        <QuerySubStatusFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          querySubStatus={row}
        />
      )}
      rowActions={{
        edit: true,
        delete: {
          onDelete: (row) => deleteQuerySubStatus.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this query sub status?',
            description: `${row.name} will be soft-deleted and hidden from lists. Leads already classified with it are unaffected.`,
          }),
        },
      }}
      bulkActions={{
        delete: {
          onDelete: async (rows) => {
            await bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
          },
          confirm: (rows) => ({
            title: `Delete ${rows.length} query sub status${rows.length === 1 ? '' : 'es'}?`,
            description: 'These query sub statuses will be soft-deleted and hidden from lists.',
          }),
        },
        setActive: {
          onSetActive: async (rows, isActive) =>
            bulkSetStatusMut.mutateAsync({ ids: rows.map((r) => r.id), isActive }),
          confirm: (rows, isActive) => ({
            title: `${isActive ? 'Activate' : 'Deactivate'} ${rows.length} query sub status${rows.length === 1 ? '' : 'es'}?`,
            description: isActive
              ? 'They will become selectable again in lead capture and reports.'
              : 'They will be hidden from new selections but remain on historical records.',
          }),
        },
      }}
      export={{
        fetchPage: (q) => listQuerySubStatuses(q),
        filename: 'query-sub-statuses',
        csv: [
          { header: 'Code', value: (r) => r.code },
          { header: 'Name', value: (r) => r.name },
          { header: 'Parent status', value: (r) => r.queryStatus.name },
          { header: 'Description', value: (r) => r.description ?? '' },
          { header: 'Color', value: (r) => r.color ?? '' },
          { header: 'Status', value: (r) => (r.isActive ? 'Active' : 'Inactive') },
          { header: 'Created at', value: (r) => r.createdAt },
        ],
      }}
      copy={{
        searchPlaceholder: 'Search by name or code (min 3 characters)',
        emptyTitle: 'No query sub statuses match these filters',
        emptyDescription: 'Adjust the parent status or active filter, or clear the search box.',
        errorTitle: 'Failed to load query sub statuses',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
